import {DateTime, FixedOffsetZone} from "luxon";
import {z} from "zod";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";

const responseValidator = z.object({
    agents: z.array(z.object({
        agentUserId: uuidZodValidator,
        averageMinutes: z.number().nonnegative().nullable(),
        totalMinutes: z.number().nonnegative(),
        intervalCount: z.number().int().nonnegative(),
    })),
});

export async function getAgentDashboardFrt(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
    from: string;
    to: string;
    timezoneOffsetMinutes: number;
    agentKeys: ReadonlyArray<string>;
}): Promise<Result<Map<string, number | null>>> {
    const agentUserIds = Array.from(new Set(args.agentKeys)).flatMap((key) => {
        const parsed = uuidZodValidator.safeParse(key);
        return parsed.success ? [parsed.data] : [];
    });
    if (agentUserIds.length === 0) {
        return dataResult(new Map<string, number | null>());
    }
    if (agentUserIds.length > 500) {
        return errorResult(new Error("Agent FRT supports at most 500 displayed agents."));
    }

    const zone = FixedOffsetZone.instance(args.timezoneOffsetMinutes);
    const from = DateTime.fromISO(args.from, {zone});
    const to = DateTime.fromISO(args.to, {zone});
    if (!from.isValid || !to.isValid || to.toMillis() <= from.toMillis()) {
        return errorResult(new Error("Invalid Agent FRT date range."));
    }
    // Analytics uses [from, to); FRT accepts inclusive business-calendar dates.
    const dateRange = {startDate: from.toISODate(), endDate: to.minus({milliseconds: 1}).toISODate()};
    const result = await robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto/agent-view/dashboard/first-response-time/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${args.accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": args.workspaceId,
            },
            body: JSON.stringify({dateRange, agentUserIds}),
        },
        responseValidator,
    );
    if (result.success !== true) {
        return result;
    }
    const averages = new Map(result.data.agents.map((agent) => [agent.agentUserId, agent.averageMinutes]));
    if (result.data.agents.length !== agentUserIds.length || averages.size !== agentUserIds.length ||
        agentUserIds.some((id) => !averages.has(id))) {
        return errorResult(new Error("Agent FRT response does not match the requested agents."));
    }
    return dataResult(averages);
}
